import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { NativeSelect, TextInput } from "@mantine/core";
import { apiClient, apiErrorMessage } from "../api/client";
import type { Employment, EntityName, Person } from "../api/types";
import type { SignedIn } from "../lib/auth";
import { absent, nothingUseful, orAbsent, shortDate } from "../lib/format";
import { applySorts, useListParams } from "../lib/useListParams";
import { useRecordEdit } from "../lib/useRecordEdit";
import { csvFilename, downloadCsv, toCsv } from "../lib/csv";
import { LookupFacts } from "../components/Enrichment";
import { ActivityTable } from "../components/ActivityTable";
import { CampaignsTable, campaignCount, useRecordCampaigns } from "../components/CampaignsTable";
import { RecordLink } from "../components/RecordLink";
import { Spinner } from "../components/Spinner";
import { ApolloFact } from "../components/CheckApollo";
import { FlagFacts } from "../components/FlagRecord";
import { OtherNamesFact } from "../components/OtherNames";
import { CompanyPicker, type PickedCompany } from "../components/CompanyPicker";
import { ReachEditor, type ContactPointRow, type ReachDraft } from "../components/ReachEditor";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { DataTable, type ColumnSpec } from "../ui/DataTable";
import { EditBar, Fact, FactGroup, Facts, useContestedFacts } from "../ui/Facts";
import { RecordPage } from "../ui/RecordPage";
import { Toolbar } from "../ui/Toolbar";
import { FUNCTION_WORDS, FunctionWord, LEVEL_WORDS, StandingWord, effectiveStandingWord, hubspotActivityWord, levelWord } from "../ui/words";

/* One person, as a page.
 *
 * The design pass, 2026-10-08. A rep opening a person is about to dial or
 * write, so the question the top of the page answers is who this is, where
 * they work now, and how to reach them: the name, the title and employer, the
 * level, function and standing as badges, and on the Details tab, which is
 * first, every way to reach them, each with a Copy link, before anything
 * else.
 *
 * Editing is one verb. Edit, among the actions, puts the Details tab into
 * edit mode: the name, the current job (title, level, function, employer,
 * and when it started), and the phones and emails become inputs, with one
 * note and one Save. The engine parses the edit into its rows
 * (api.edit_record): a new employer ends the old job and starts the new one,
 * a removed number is marked wrong and never deleted. The person editing
 * sees neither. docs/ux-decisions.md, Records: viewing and editing.
 *
 * The employer's do-not-contact reaches this page as a banner, because a rep
 * who opens a person at a company that asked not to be called must not see
 * four ways to reach them and no warning. api.person does not return it, so
 * the page reads the employer's row. Found in the 2026-10-08 audit.
 *
 * The employment history is the part worth a table of its own, because a
 * title that moved is the most common thing a rep finds out on a call. A
 * tab for the buildings a person is tied to waits on an engine view, since
 * api.contact names the property's id and not its name. */

type Tool = "edit" | "lookup" | "flag" | null;

type JobDraft = {
  title: string;
  level: string;
  job_function: string;
  company: PickedCompany | null;
  effective_from: string;
};

const safe = (q: string) => q.replace(/[,()*\\]/g, " ").trim();

export function PersonPage({ session }: { session: SignedIn }) {
  const { id = "" } = useParams();
  const token = session.idToken;
  const [tool, setTool] = useState<Tool>(null);

  const person = useQuery({
    queryKey: ["person", id],
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("person").select("*").eq("id", id).limit(1);
      if (error) throw new Error(apiErrorMessage(error));
      return ((data ?? [])[0] ?? null) as Person | null;
    },
  });

  const names = useQuery({
    queryKey: ["person-names", id],
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("entity_name")
        .select("name,source,first_seen,seen_count")
        .eq("entity_type", "person").eq("entity_id", id)
        .order("seen_count", { ascending: false }).limit(50);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as EntityName[];
    },
  });

  const jobCount = useQuery({
    queryKey: ["person-employment", "count", id],
    queryFn: async () => {
      const { error, count } = await apiClient(token)
        .from("employment").select("id", { count: "exact", head: true }).eq("person_id", id);
      if (error) throw new Error(apiErrorMessage(error));
      return count ?? null;
    },
  });

  const openFlags = useQuery({
    queryKey: ["flags-open", "person", id],
    queryFn: async () => {
      const { error, count } = await apiClient(token)
        .from("record_flag").select("id", { count: "exact", head: true })
        .eq("entity_type", "person").eq("entity_id", id).is("resolved_at", null);
      if (error) throw new Error(apiErrorMessage(error));
      return count ?? null;
    },
  });

  const p = person.data;
  const employerId = p?.employer_id ?? null;

  // The employer's do-not-contact, which api.person does not return.
  const employer = useQuery({
    queryKey: ["company-dnc", employerId],
    enabled: Boolean(employerId),
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("company")
        .select("id,name,do_not_contact,do_not_contact_reason").eq("id", employerId as string).limit(1);
      if (error) throw new Error(apiErrorMessage(error));
      return ((data ?? [])[0] ?? null) as { id: string; name: string | null; do_not_contact: boolean | null; do_not_contact_reason: string | null } | null;
    },
  });

  const campaigns = useRecordCampaigns(token, "person", id);
  const nCampaigns = campaignCount(campaigns.data);
  const newestCampaign = campaigns.data?.[0];

  const otherNames = (names.data ?? []).map((n) => n.name).filter((n): n is string => Boolean(n) && n !== p?.full_name);
  const hubspot = p?.hubspot_status;
  const dnc = employer.data?.do_not_contact ? employer.data : null;

  return (
    <RecordPage
      backLabel="Back to people" backTo="/people" fromKey="people"
      loading={person.isLoading}
      loadedAt={person.dataUpdatedAt} refreshing={person.isFetching} onRefresh={() => person.refetch()}
      error={person.isError ? (person.error as Error).message : null}
      notFound={!p && !person.isLoading ? "No person with that id. They may have been merged." : null}
      banner={dnc ? (
        <p className="banner refusal rp-dnc" role="alert">
          Do not contact: {dnc.name ?? "the employer"} is marked do not contact{dnc.do_not_contact_reason ? ` (${dnc.do_not_contact_reason})` : ""}.
        </p>
      ) : null}
      title={p?.full_name ?? "Person"}
      aka={otherNames}
      facts={p ? (
        <>
          {orAbsent(p.title)}
          {p.employer_name ? <>{" · "}<RecordLink to="companies" id={p.employer_id} label={p.employer_name} /></> : null}
          {/* The why-call in one line: the campaigns this person's buildings
              were in, with the newest named. */}
          {nCampaigns && newestCampaign ? (
            <>
              {" · "}
              <Link to="?tab=campaigns">In {nCampaigns.toLocaleString()} campaign{nCampaigns === 1 ? "" : "s"}</Link>
              <span className="muted">, last {shortDate(newestCampaign.frozen_at)}{newestCampaign.property_name ? ` for ${newestCampaign.property_name}` : ""}</span>
            </>
          ) : null}
        </>
      ) : null}
      badges={p ? (
        <>
          {levelWord(p.level) ? <Badge tone="plain">{levelWord(p.level) as string}</Badge> : null}
          {p.job_function ? <Badge tone="plain"><FunctionWord fn={p.job_function} /></Badge> : null}
          {p.standing === "current" ? <Badge tone="ok">Current job</Badge>
            : p.standing === "ended" ? <Badge tone="quiet">Job ended</Badge>
            : <Badge tone="quiet" title="Not confirmed">Job not confirmed</Badge>}
          {hubspot === "yes" ? <Badge tone="plain">In HubSpot</Badge>
            : hubspot === "not checked" ? <Badge tone="quiet" title="Not checked against HubSpot yet">HubSpot not checked</Badge>
            : <Badge tone="quiet" title="Not found in HubSpot">Not in HubSpot</Badge>}
          {p.hubspot_last_contacted_at ? <Badge tone="ok" title="When Buzze last contacted this person, from HubSpot">Contacted {shortDate(p.hubspot_last_contacted_at)}</Badge> : null}
          {openFlags.data ? <Badge tone="waiting" title="Somebody has said this record is wrong">Flagged ({openFlags.data})</Badge> : null}
        </>
      ) : null}
      actions={[
        { label: "Edit", tab: "details", onSelect: () => setTool("edit") },
        { label: "Run web enrichment", tab: "details", anchor: "lookup", onSelect: () => setTool("lookup") },
        { label: "Run Apollo enrichment", tab: "details", anchor: "apollo" },
        { label: "Flag this record", tab: "details", anchor: "flag", onSelect: () => setTool("flag") },
      ]}
      figures={[
        { n: nCampaigns, label: "campaigns", tab: "campaigns", pending: campaigns.isPending },
        { n: jobCount.data ?? null, label: "jobs on record", tab: "employment", pending: jobCount.isPending },
        { n: p?.source_count, label: "sources", tab: "activity" },
      ]}
      defaultTab="details"
      tabs={[
        { key: "details", label: "Details", content: p ? <DetailsTab token={token} id={id} person={p} tool={tool} setTool={setTool} onChanged={() => { person.refetch(); jobCount.refetch(); }} /> : null },
        { key: "campaigns", label: "Campaigns", count: nCampaigns, content: <CampaignsTable token={token} entityType="person" entityId={id} /> },
        { key: "employment", label: "Employment", count: jobCount.data ?? null, content: <EmploymentTab token={token} id={id} /> },
        { key: "activity", label: "Activity", count: p?.source_count, content: <ActivityTable token={token} entityType="person" entityId={id} /> },
      ]}
    />
  );
}

/* One way to reach somebody: the value as a tel: or mailto: link, which is
   what a phone wants, and a Copy link beside it, which is what a desk wants,
   because the team dials from Orum and a dialer opening on a Mac is in the
   way. */
function ReachValue({ kind, value, bounced }: { kind: string; value: string; bounced?: boolean }) {
  const [copied, setCopied] = useState(false);
  const href = kind === "email" ? `mailto:${value}` : `tel:${value.replace(/[^+0-9]/g, "")}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // The value is on screen to select by hand. A browser that refuses the
      // clipboard says so in its own way.
    }
  };
  return (
    <span className="fact-reach">
      <a href={href} className={bounced ? "rp-reach muted" : "rp-reach"}>{value}</a>
      {bounced ? <span className="muted"> (bounced)</span> : null}
      <Button kind="link" size="compact-xs" className="fact-copy" aria-label={`Copy ${value}`} onClick={copy}>
        {copied ? "Copied" : "Copy"}
      </Button>
    </span>
  );
}

/* The record's facts, in four groups, in the order a rep needs them: how to
   reach them, who they are, where they stand with Buzze, and what EVIE's
   data looks like. In edit mode the reach list, the name and the current
   job become inputs and the tools step aside until Save or Cancel. */
function DetailsTab({ token, id, person: p, tool, setTool, onChanged }: {
  token: string; id: string; person: Person; tool: Tool; setTool: (t: Tool) => void; onChanged: () => void;
}) {
  const editing = tool === "edit";

  /* Every phone and email the lake has for this person, one row each with
     its id (api.person_contact_point, engine migration 189). api.person
     returns only whether each kind exists, because a list of fifty people
     must not pull forty thousand mobile numbers over the wire to draw a
     tick, and api.contact collapses to one per kind, which hides the second
     mobile a rep needs when the first is dead. */
  const points = useQuery({
    queryKey: ["person-contact-points", id],
    queryFn: async () => {
      const { data, error } = await apiClient(token)
        .from("person_contact_point").select("id,person_id,kind,value,verification_status,created_at")
        .eq("person_id", id).order("kind", { ascending: true }).order("created_at", { ascending: true })
        .limit(50);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as ContactPointRow[];
    },
  });
  const contested = useContestedFacts(token, "person", id);

  const ofKind = (kinds: string[]) => {
    if (points.isPending) return <Spinner />;
    const rows = (points.data ?? []).filter((x) => x.kind && kinds.includes(x.kind) && x.value);
    if (!rows.length) return absent();
    return rows.map((x) => <ReachValue key={x.id} kind={x.kind as string} value={x.value as string} bounced={x.verification_status === "bounced"} />);
  };

  // The job as the form starts from: the current job, and no start date,
  // because a date the editor does not give is not a date.
  const baseJob = (): JobDraft => ({
    title: p.title ?? "",
    level: p.level ?? "",
    job_function: p.job_function ?? "",
    company: p.employer_id && p.employer_name ? { id: p.employer_id, name: p.employer_name } : null,
    effective_from: "",
  });
  const [jobDraft, setJobDraft] = useState<JobDraft | null>(null);
  const job = jobDraft ?? baseJob();
  const [reach, setReach] = useState<ReachDraft>({ add: [], remove: [] });
  const [saved, setSaved] = useState<string[]>([]);

  const jobChanges = () => {
    const base = baseJob();
    const out: Record<string, string> = {};
    if (job.company && job.company.id !== base.company?.id) out.company_id = job.company.id;
    if (job.title !== base.title) out.title = job.title;
    if (job.level !== base.level) out.level = job.level;
    if (job.job_function !== base.job_function) out.job_function = job.job_function;
    if (job.effective_from) out.effective_from = job.effective_from;
    return out;
  };
  const moving = Boolean(job.company && job.company.id !== baseJob().company?.id);

  const edit = useRecordEdit({
    token, entityType: "person", entityId: id,
    values: p as unknown as Record<string, unknown>,
    extra: () => {
      const j = jobChanges();
      return { job: j, reach, count: Object.keys(j).length + reach.add.length + reach.remove.length };
    },
    onSaved: (result) => {
      setSaved(result.fields);
      window.setTimeout(() => setSaved([]), 4000);
      setJobDraft(null);
      setReach({ add: [], remove: [] });
      setTool(null);
      onChanged();
      points.refetch();
    },
  });
  const cancel = () => { edit.reset(); setJobDraft(null); setReach({ add: [], remove: [] }); setTool(null); };

  const hubspot = p.hubspot_status;
  const toolProps = (t: Exclude<Tool, null | "edit">) => ({ open: tool === t, onOpenChange: (o: boolean) => setTool(o ? t : null) });

  return (
    <section className="rp-tabbody" aria-label="Details">
      {editing ? (
        <EditBar pending={edit.pending} saving={edit.saving} error={edit.error}
                 note={edit.note} onNote={edit.setNote} onSave={edit.save} onCancel={cancel} />
      ) : null}
      <Facts token={token} entityType="person" values={p as unknown as Record<string, unknown>}
             editing={editing} draft={edit.draft} onDraft={edit.setField}
             contested={contested.data ?? {}} onUseClaim={edit.applyClaim} saved={saved}>
        <FactGroup title="How to reach them">
          {editing ? (
            <Fact label="Phones and emails" edit={<ReachEditor points={points.data ?? []} draft={reach} onChange={setReach} />}>{null}</Fact>
          ) : (
            <>
              <Fact label="Mobile">{ofKind(["mobile"])}</Fact>
              <Fact label="Email">{ofKind(["email"])}</Fact>
              <Fact label="Office line">{ofKind(["phone", "office"])}</Fact>
            </>
          )}
          <Fact label="LinkedIn">
            {p.linkedin_url ? <a href={p.linkedin_url} target="_blank" rel="noreferrer" className="rp-reach">profile</a> : absent()}
          </Fact>
          {editing ? null : <ApolloFact token={token} entityType="person" entityId={id} onFilled={() => { onChanged(); points.refetch(); }} />}
        </FactGroup>

        <FactGroup title="Who they are">
          <Fact label="Name" field="full_name">{p.full_name}</Fact>
          <Fact label="Employer"
                edit={<CompanyPicker token={token} value={job.company} onChange={(c) => setJobDraft({ ...job, company: c })} />}>
            <RecordLink to="companies" id={p.employer_id} label={p.employer_name} />
          </Fact>
          {editing && moving ? (
            <div className="fg-note muted">
              This ends the job at {baseJob().company?.name ?? "the current employer"} and starts one at {job.company?.name}.
            </div>
          ) : null}
          <Fact label="Title"
                edit={<TextInput aria-label="Title" value={job.title} w="18rem" onChange={(e) => setJobDraft({ ...job, title: e.currentTarget.value })} />}>
            {orAbsent(p.title)}
          </Fact>
          <Fact label="Level"
                edit={<NativeSelect aria-label="Level" value={job.level} onChange={(e) => setJobDraft({ ...job, level: e.currentTarget.value })}
                                    data={[{ value: "", label: "Not set" }, ...Object.entries(LEVEL_WORDS).map(([value, label]) => ({ value, label }))]} />}>
            {orAbsent(levelWord(p.level))}
          </Fact>
          <Fact label="Function"
                edit={<NativeSelect aria-label="Function" value={job.job_function} onChange={(e) => setJobDraft({ ...job, job_function: e.currentTarget.value })}
                                    data={[{ value: "", label: "Not set" }, ...Object.entries(FUNCTION_WORDS).map(([value, label]) => ({ value, label }))]} />}>
            <FunctionWord fn={p.job_function} />
          </Fact>
          {editing ? (
            <Fact label="Since"
                  edit={(
                    <span className="fact-inline">
                      <TextInput type="date" aria-label="Since" value={job.effective_from} w="11rem"
                                 onChange={(e) => setJobDraft({ ...job, effective_from: e.currentTarget.value })} />
                      <span className="muted">Leave it blank if you do not know.</span>
                    </span>
                  )}>
              {null}
            </Fact>
          ) : null}
          <Fact label="Standing"><StandingWord standing={p.standing} /></Fact>
          <Fact label="Confirmed standing">{orAbsent(effectiveStandingWord(p.effective_standing))}</Fact>
          <Fact label="Last confirmed">{shortDate(p.last_confirmed_at)}</Fact>
          <Fact label="Relevance">{p.employment_relevance ?? absent("not classified")}</Fact>
          <OtherNamesFact token={token} entityType="person" entityId={id} currentName={p.full_name} />
        </FactGroup>

        <FactGroup title="With Buzze">
          <Fact label="In HubSpot">
            {hubspot === "yes" ? "yes"
              : hubspot === "not checked" ? <span title="Not checked against HubSpot yet">not checked</span>
              : nothingUseful("Not found in HubSpot")}
          </Fact>
          <Fact label="HubSpot checked">{shortDate(p.hubspot_checked_at)}</Fact>
          {/* From HubSpot's own properties through the crosswalk (engine
              migration 184). Absent means HubSpot has nothing logged or the
              crosswalk has not read it yet, which are told apart by the
              checked date above. */}
          <Fact label="Last contacted">
            {p.hubspot_last_contacted_at
              ? <>{shortDate(p.hubspot_last_contacted_at)}{p.hubspot_contact_count ? <span className="muted"> · {p.hubspot_contact_count.toLocaleString()} time{p.hubspot_contact_count === 1 ? "" : "s"}</span> : null}</>
              : absent("-", "No contact logged in HubSpot, or not read from it yet")}
          </Fact>
          <Fact label="Last activity">
            {p.hubspot_last_activity_at
              ? <>{shortDate(p.hubspot_last_activity_at)}{hubspotActivityWord(p.hubspot_last_activity) ? <span className="muted"> · {hubspotActivityWord(p.hubspot_last_activity)}</span> : null}</>
              : absent("-", "No sales activity logged in HubSpot, or not read from it yet")}
          </Fact>
          <Fact label="HubSpot owner">{orAbsent(p.hubspot_owner)}</Fact>
          {editing ? null : <FlagFacts token={token} entityType="person" entityId={id} {...toolProps("flag")} />}
        </FactGroup>

        <FactGroup title="What EVIE knows">
          {editing ? null : <LookupFacts token={token} entityType="person" entityId={id} {...toolProps("lookup")} />}
          <Fact label="Sources">{p.source_count ?? absent()}</Fact>
          <Fact label="Source tiers">{p.source_tiers?.join(", ") || absent()}</Fact>
          <Fact label="Signals">{p.signal_count ?? absent()}</Fact>
          <Fact label="Newest source">{shortDate(p.newest_source_date)}</Fact>
          <Fact label="First seen">{shortDate(p.created_at)}</Fact>
          <Fact label="Last changed">{shortDate(p.updated_at)}</Fact>
          <Fact label="Id">{p.id}</Fact>
        </FactGroup>
      </Facts>
    </section>
  );
}

const JOBS_CSV = ["company_name", "title", "level", "job_function", "standing", "effective_standing", "effective_from", "effective_to", "last_confirmed_at"];

/* Every job on record, as a table, newest confirmed first. The current job
   is corrected from the Details tab's Edit, which ends it and starts a new
   one when the employer changes. The history is read here. */
function EmploymentTab({ token, id }: { token: string; id: string }) {
  const params = useListParams("last_confirmed_at", [], false, "", "e.");
  const q = safe(params.search);

  const jobs = useQuery({
    queryKey: ["person-employment", id, params.sorts, q],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      let query = apiClient(token)
        .from("employment")
        .select("id,company_id,company_name,title,level,job_function,standing,effective_standing,effective_from,effective_to,last_confirmed_at,relevance")
        .eq("person_id", id);
      if (q) query = query.or(`company_name.ilike.*${q}*,title.ilike.*${q}*`);
      const { data, error } = await applySorts(query, params.sorts).order("id", { ascending: true }).limit(100);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as Employment[];
    },
  });

  const rows = jobs.data ?? [];

  const columns: ColumnSpec<Employment>[] = [
    { id: "company", header: "Company", role: "leading", fields: ["company_name"], sort: "company_name", title: true, narrowLabel: null,
      cell: (j) => <RecordLink to="companies" id={j.company_id} label={j.company_name} /> },
    { id: "title", header: "Title", role: "leading", fields: ["title"], sort: "title", narrowLabel: null, cell: (j) => orAbsent(j.title) },
    { id: "level", header: "Level", role: "secondary", fields: ["level"], sort: "level", narrowLabel: null, cell: (j) => orAbsent(levelWord(j.level)) },
    { id: "job_function", header: "Function", role: "secondary", fields: ["job_function"], sort: "job_function", narrowLabel: null,
      cell: (j) => <FunctionWord fn={j.job_function} /> },
    { id: "standing", header: "Standing", role: "secondary", fields: ["standing"], sort: "standing", narrowLabel: null,
      cell: (j) => <StandingWord standing={j.standing} unconfirmed={j.effective_standing === "presumed_current" && j.standing === "current"} /> },
    { id: "confirmed", header: "Confirmed", role: "secondary", fields: ["last_confirmed_at"], sort: "last_confirmed_at", sortDescendingFirst: true,
      cell: (j) => shortDate(j.last_confirmed_at) },
    { id: "from", header: "From", role: "detail", fields: ["effective_from"], sort: "effective_from", sortDescendingFirst: true, cell: (j) => shortDate(j.effective_from) },
    { id: "to", header: "To", role: "detail", fields: ["effective_to"], sort: "effective_to", sortDescendingFirst: true, cell: (j) => shortDate(j.effective_to) },
  ];

  return (
    <section className="rp-tabbody" aria-label="Employment">
      <Toolbar
        id="employment"
        search={{ label: "Search by company or title", value: params.search, onSubmit: (s) => params.set({ search: s }) }}
        onClearAll={() => params.set({ search: "" })}
        right={rows.length ? (
          <Button size="compact-sm" onClick={() => downloadCsv(csvFilename("employment"),
            toCsv(JOBS_CSV, rows as unknown as Record<string, unknown>[]))}>
            CSV
          </Button>
        ) : null}
      />
      <p className="muted rp-count">Every job on record, newest confirmed first. The current job is changed with Edit on the Details tab.</p>
      {jobs.isLoading ? <p className="muted"><Spinner />Loading.</p>
        : jobs.isError ? <p className="refusal">{(jobs.error as Error).message}</p>
        : !rows.length ? <p className="muted">{q ? "No job matches that search." : "No jobs on record. Edit on the Details tab starts one, and Run Apollo enrichment adds a title and an email when it finds the person."}</p>
        : <DataTable columns={columns} rows={rows} rowKey={(j) => j.id ?? ""} params={params} />}
    </section>
  );
}
